import { XClose } from '@untitledui/icons'
import type { ReactNode } from 'react'
import { Dialog, Modal, ModalOverlay } from '@/components/application/modals/modal'
import { cx } from '@/utils/cx'

/**
 * Ein Dialogfenster, wie es alle Oberflächen benutzen: Untitled UIs Modal mit
 * Kopf (Titel, Satz, Schließen-Kreuz) und einem Rumpf, der **innen** scrollt.
 *
 * **Warum ein eigenes Teil:** Scrollt der ganze Dialog, läuft der Rollbalken
 * über die runde Ecke des Fensters, und die Ecke wirkt abgeschnitten. Hier
 * bleibt der Kopf stehen, der Rumpf scrollt, und die Ecken bleiben rund
 * (`overflow-hidden` am Fenster). Das Kreuz oben rechts schließt — nicht nur
 * Escape und der Klick daneben.
 */
export function Dialogfenster({
  titel,
  satz,
  onSchliessen,
  breite = 'w-[min(32rem,calc(100vw-2rem))]',
  children,
  fuss,
}: {
  titel: string
  satz?: ReactNode
  onSchliessen: () => void
  breite?: string
  children: ReactNode
  /** Schaltflächen unter dem Rumpf; stehen fest, scrollen nicht mit. */
  fuss?: ReactNode
}) {
  return (
    <ModalOverlay isOpen isDismissable onOpenChange={(offen) => !offen && onSchliessen()}>
      <Modal className={cx('overflow-hidden', breite)}>
        <Dialog aria-label={titel} className="flex max-h-[inherit] flex-col overflow-hidden">
          <div className="flex items-start gap-3 px-6 pt-6 pb-4">
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-lg font-bold text-primary">{titel}</h2>
              {satz ? <p className="mt-1 text-sm text-tertiary">{satz}</p> : null}
            </div>
            <button
              type="button"
              onClick={onSchliessen}
              aria-label="Schließen"
              className="-mt-1 -mr-2 grid size-9 shrink-0 cursor-pointer place-items-center rounded-lg text-quaternary transition-colors hover:bg-secondary hover:text-primary focus-visible:outline-2 focus-visible:outline-akzent"
            >
              <XClose className="size-5" />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-2">
            {children}
          </div>
          {fuss ? (
            <div className="flex flex-wrap justify-end gap-2 px-6 pt-4 pb-6">{fuss}</div>
          ) : null}
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}
